import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AlertService,
    AppConfigService,
    ClaimService,
    DateParserFormatterService,
    ENDPOINTS_CONFIG,
    ErrorStateService,
    GlobalConstants,
    GuidService,
    LoggingService,
} from '@app-core';
import { Address, AddressType, Claim, ClaimPhysician, Country, Physician, State, Student } from '@app-models';
import { FieldErrorDirective, PhoneValidator, SignaturePadComponent, ZipCodeValidator } from '@app-shared';
import { NgbDateParserFormatter, NgbDatepickerModule, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FraudNoticesComponent } from 'app/fraud-notices/fraud-notices.component';
import { NgxMaskDirective } from 'ngx-mask';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-physician-response',
    templateUrl: './physician-response.component.html',
    styleUrls: ['./physician-response.component.scss'],
    imports: [ReactiveFormsModule, FieldErrorDirective, NgxMaskDirective, NgbDatepickerModule, SignaturePadComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{ provide: NgbDateParserFormatter, useClass: DateParserFormatterService }],
})
export class PhysicianResponseComponent implements OnInit, OnDestroy {
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly claimService = inject(ClaimService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly spinnerService = inject(NgxSpinnerService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dateParser = inject(DateParserFormatterService);
    private readonly guidService = inject(GuidService);
    private readonly alertService = inject(AlertService);
    private readonly errorStateService = inject(ErrorStateService);
    private readonly modalService = inject(NgbModal);
    private claimSubscription: Subscription;
    claimTypes = GlobalConstants.CLAIM_TYPE;
    claimStatus = GlobalConstants.CLAIM_STATUS;
    claim: Claim | null = null;
    claimForm: FormGroup;
    originalFormValues: any;
    states: State[];
    filteredStates: State[];
    countries: Country[];
    stateCaption: string = 'State';
    postalCodeCaption: string = 'Zip code';
    showAddress2: boolean = false;
    minValidDate: NgbDateStruct | null;
    maxValidDate: NgbDateStruct | null;
    minConsultDate: NgbDateStruct | null;
    settlementAuthorizationText: string = GlobalConstants.FORM_TEXT.SETTLEMENT_AUTHORIZATION;
    authorizationText: string = GlobalConstants.FORM_TEXT.AUTHORIZATION;
    instructionText: string = GlobalConstants.FORM_TEXT.PHYSICIAN_INSTRUCTION;
    signatureText: string = GlobalConstants.FORM_TEXT.SIGNATURE_AKNOWLEDGEMENT;
    withdrawalSeparationDateText: string = GlobalConstants.SEPARATION_DATE_DEFINITIONS.WITHDRAWAL;
    dismissalSeparationDateText: string = GlobalConstants.SEPARATION_DATE_DEFINITIONS.DISMISSAL;

    // Character counts for each textarea field
    characterCounts = {
        physicianSeparationReason: 0,
        physicianReferredPhysicianInfo: 0,
        physicianReferringPhysicianInfo: 0,
        physicianWithdrawalRecommendationReason: 0,
        physicianAlternateActivityExplanation: 0,
        physicianAnticipatedReturnDescription: 0,
    };

    isSaving: boolean = false;

    get formControls() {
        return this.claimForm.controls;
    }

    ngOnInit(): void {
        this.fetchClaimData();
    }

    fetchClaimData(): void {
        this.states = this.appConfigService.getLookupData(ENDPOINTS_CONFIG.states);
        this.countries = this.appConfigService.getLookupData(ENDPOINTS_CONFIG.countries);
        this.minValidDate = this.getMinimumSeparationDate();
        this.maxValidDate = this.getMaximumSeparationDate();
        this.minConsultDate = this.getMinimumConsultDate();
        this.initializeClaimForm();

        this.claimSubscription = this.claimService.getCurrentClaim().subscribe((claim: Claim) => {
            if (claim) {
                this.claim = claim;
                this.claimForm.patchValue(this.claim);
                this.updateCountryCaptions(this.claim.accountParent?.addresses?.[0]?.countryISO3 || 'USA');
                // if (this.claim.parentSeparationDate) {
                //     const parsedSeparationDate = this.dateParser.parse(this.claim.parentSeparationDate);
                //     this.claimForm.get('parentSeparationDate').setValue(parsedSeparationDate);
                // }
                // TEST - pre populate form for testing
                //this.populateTestForm();
            }
        });
    }

    /*     populateTestForm() {
        // create a test object populate the form with the values - use the ClaimPhysician model as the object
        const testClaimPhysician = new ClaimPhysician();
        testClaimPhysician.physicianStudent = new Student();
        testClaimPhysician.physicianStudent.firstName = 'John';
        testClaimPhysician.physicianStudent.lastName = 'Doe';
        testClaimPhysician.physicianSeparationReason = 'Medical Leave';
        testClaimPhysician.physicianICDDSM = 'F32.0';
        testClaimPhysician.physicianClassAbsenceStartDate = '2023-01-01';
        testClaimPhysician.physicianClassAbsenceEndDate = '2023-01-15';
        testClaimPhysician.physicianFirstConsultDate = '2023-01-01';
        testClaimPhysician.physicianLastConsultDate = '2023-01-15';
        testClaimPhysician.physicianHomeVisitCount = '2';
        testClaimPhysician.physicianOfficeVisitCount = '3';
        testClaimPhysician.physicianHospitalVisitCount = '1';
        testClaimPhysician.physicianStillUnderCare = true;
        testClaimPhysician.physicianReferredPhysicianInfo = 'Dr. Smith, 123-456-7890';
        testClaimPhysician.physicianReferringPhysicianInfo = 'Dr. Brown, 987-654-3210';
        testClaimPhysician.physicianWithdrawnOnRecommendation = false;
        testClaimPhysician.physicianWithdrawalRecommendationReason = '';
        testClaimPhysician.physicianAnticipatedReturnDescription = 'Expected to return by 2023-02-01';
        testClaimPhysician.physicianAlternateSchoolOrEmploymentDuringLeave = false;
        testClaimPhysician.physicianWithPhysicianPermission = true;
        testClaimPhysician.physicianAlternateActivityStartDate = '2023-01-16';
        testClaimPhysician.physicianAlternateActivityExplanation = 'Light exercise';
        testClaimPhysician.physician = new Physician();
        testClaimPhysician.physician.firstName = 'Alice';
        testClaimPhysician.physician.lastName = 'Johnson';
        testClaimPhysician.physician.emailAddress = 'cartis@obitest.com';
        testClaimPhysician.physician.phoneNumber = '555-123-4567';
        testClaimPhysician.physician.address = new Address();
        testClaimPhysician.physician.address.address1 = '123 Main St';
        testClaimPhysician.physician.address.address2 = 'Apt 4B';
        testClaimPhysician.physician.address.city = 'Anytown';
        testClaimPhysician.physician.address.stateAbbreviation = 'NY';
        testClaimPhysician.physician.address.countryISO3 = 'USA';
        testClaimPhysician.physician.address.postalCode = '12345';
        testClaimPhysician.physicianLicenseNumber = 'MD123456';
        //testClaimPhysician.physicianSubmittedDate = new Date('2023-01-15');
        // populate this.claimForm with the testClaimPhysicna data
        testClaimPhysician.physicianSignature =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAYAAACtWK6eAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAALEgAACxIB0t1+/AAAADl0RVh0U29mdHdhcmUAQWRvYmUgSW1hZ2VSZWFkeXHJZTwAAAXWSURBVHic7d3NaxRHFAbgP3P//zO7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7e3+H4j8yM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzMzsxM3MzOzhf4H8D8J+FfgD4D8BPAfgX4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BfAPwH4F8A/AfAvwH4F+BPAPwHwL8B+BeC/9X5mZmZm';
        this.claimForm.patchValue(testClaimPhysician);

        this.originalFormValues = this.claimForm.value;
        //this.changeDetector.detectChanges();

        // create this.claimForm.patchValue() entries for all date fields using the convertStringToNgbDate method
        this.claimForm.patchValue({
            physicianClassAbsenceStartDate: this.convertStringToNgbDate(testClaimPhysician.physicianClassAbsenceStartDate),
            physicianClassAbsenceEndDate: this.convertStringToNgbDate(testClaimPhysician.physicianClassAbsenceEndDate),
            physicianFirstConsultDate: this.convertStringToNgbDate(testClaimPhysician.physicianFirstConsultDate),
            physicianLastConsultDate: this.convertStringToNgbDate(testClaimPhysician.physicianLastConsultDate),

            physicianAlternateActivityStartDate: this.convertStringToNgbDate(testClaimPhysician.physicianAlternateActivityStartDate),
        });
    }
 */
    // If you have a date string like "2024-03-15" from your API:
    /*   private convertStringToNgbDate(dateString: string): NgbDateStruct | null {
        if (!dateString) return null;

        const parts = dateString.split('-');
        return {
            year: parseInt(parts[0], 10),
            month: parseInt(parts[1], 10),
            day: parseInt(parts[2], 10),
        };
    } */
    initializeClaimForm() {
        this.claimForm = this.formBuilder.group({
            physicianStudent: this.formBuilder.group({
                firstName: ['', [Validators.required, Validators.maxLength(50)]],
                lastName: ['', [Validators.required, Validators.maxLength(50)]],
            }),
            physicianSeparationReason: ['', [Validators.required, Validators.maxLength(1000)]],
            physicianICDDSM: [''],
            physicianClassAbsenceStartDate: ['', Validators.required],
            physicianClassAbsenceEndDate: [null],
            physicianFirstConsultDate: ['', Validators.required],
            physicianLastConsultDate: ['', Validators.required],
            physicianHomeVisitCount: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
            physicianOfficeVisitCount: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
            physicianHospitalVisitCount: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
            physicianStillUnderCare: ['', Validators.required],
            physicianReferredPhysicianInfo: ['', [Validators.maxLength(255)]],
            physicianReferringPhysicianInfo: ['', [Validators.maxLength(255)]],
            physicianWithdrawnOnRecommendation: ['', Validators.required],
            physicianWithdrawalRecommendationReason: ['', [Validators.maxLength(1000)]],
            physicianAnticipatedReturnDescription: ['', [Validators.maxLength(255)]],
            physicianAlternateSchoolOrEmploymentDuringLeave: [''],
            physicianWithPhysicianPermission: [''],
            physicianAlternateActivityStartDate: [null],
            physicianAlternateActivityExplanation: ['', [Validators.maxLength(255)]],
            physician: this.formBuilder.group({
                firstName: ['', [Validators.required, Validators.maxLength(50)]],
                lastName: ['', [Validators.required, Validators.maxLength(50)]],
                emailAddress: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
                phoneNumber: ['', [Validators.required, PhoneValidator.phone()]],
                address: this.formBuilder.group({
                    address1: ['', [Validators.required, Validators.maxLength(200)]],
                    address2: ['', [Validators.maxLength(200)]],
                    city: ['', [Validators.required, Validators.maxLength(100)]],
                    stateAbbreviation: ['', Validators.required],
                    countryISO3: ['USA', Validators.required],
                    postalCode: ['', [Validators.required, Validators.maxLength(10), ZipCodeValidator.validate('USA')]],
                }),
            }),
            physicianLicenseNumber: ['', [Validators.required, Validators.maxLength(20)]],
            //physicianSubmittedDate: ['', Validators.required],
            physicianSignature: ['', Validators.required],
        });

        // Initialize character counts for existing form values
        this.initializeCharacterCounts();
        this.onValueChanges();
    }

    // track any changes of radio buttons
    onValueChanges(): void {
        this.claimForm.get('physicianWithPhysicianPermission').valueChanges.subscribe((value) => {
            if (value === true) {
                this.claimForm.controls['physicianAlternateActivityStartDate'].setValidators(Validators.required);
            } else {
                this.claimForm.controls['physicianAlternateActivityStartDate'].patchValue(null);
                this.claimForm.controls['physicianAlternateActivityStartDate'].clearValidators();
            }
            this.claimForm.controls['physicianAlternateActivityStartDate'].updateValueAndValidity();
        });
    }

    public validateDateRange(dateField1: string, dateField2: string): void {
        const startDate = new Date(this.dateParser.formatForJava(this.formControls[dateField1].value));
        const endDate = new Date(this.dateParser.formatForJava(this.formControls[dateField2].value));
        if (Number.isNaN(startDate.valueOf()) || Number.isNaN(endDate.valueOf())) {
            // Either value is not set - don't validate
            return;
        } else if (startDate.valueOf() >= endDate.valueOf()) {
            this.formControls[dateField2].setErrors({ invalidDateRange: true });
            return;
        }
        this.formControls[dateField2].setErrors(null); // Clear any previous errors
    }

    public validateDateRangeCrossField(dateField1: string, dateField2: string): boolean {
        //this.validateDateRangeCrossField('physicianLastConsultDate', 'physicianClassAbsenceEndDate');
        //
        // (ngModelChange)="validateDateRange('physicianClassAbsenceStartDate', 'physicianClassAbsenceEndDate')" />
        //(ngModelChange)="validateDateRange('physicianFirstConsultDate', 'physicianLastConsultDate')" />

        const consultDate = new Date(this.dateParser.formatForJava(this.formControls[dateField1].value));

        const absenceDate = new Date(this.dateParser.formatForJava(this.formControls[dateField2].value));

        const formattedConsult = this.formatDateToYYYYMMDD(consultDate);
        const formattedAbsence = this.formatDateToYYYYMMDD(absenceDate);
        if (Number.isNaN(consultDate.valueOf()) || Number.isNaN(absenceDate.valueOf())) {
            // Either value is not set - don't validate
            return true;
        } else if (formattedConsult > formattedAbsence) {
            this.formControls[dateField1].setErrors({ invalidDateConsult: true });
            return false;
        } /* else if (startDate.valueOf() >= endDate.valueOf()) {
            this.formControls[dateField2].setErrors({ invalidDateSpan: true });
            return;
        } */
        return true;
    }
    private formatDateToYYYYMMDD(date: Date): number {
        if (!date || Number.isNaN(date.getTime())) return Number.NaN;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return Number(`${year}${month}${day}`);
    }

    toggleAddress2() {
        this.showAddress2 = !this.showAddress2;
    }

    onCountryChange(event: any) {
        const countryISO3 = event.target.value;
        this.updateCountryCaptions(countryISO3);
        this.applyZipCodeValidator(countryISO3);
    }

    onSubmit() {
        this.isSaving = true;
        this.markFormGroupTouched(this.claimForm);

        if (this.claimForm.valid) {
            const claimData = this.buildClaimData();
            this.claimService.updatePhysicianClaim(claimData, this.claim.id).subscribe({
                next: (response) => {
                    LoggingService.log('Application Updated', response);
                    this.claimService.setSubmissionSuccessful(true);
                    this.isSaving = false;
                    this.router.navigate(['/success'], { skipLocationChange: true });
                },
                error: (error) => {
                    this.claimService.setSubmissionSuccessful(false);
                    this.isSaving = false;
                    LoggingService.error('Error Updating Application', error);
                },
            });
        } else {
            // Reset isSaving if form is invalid
            this.isSaving = false;
            // Scroll to and focus the first invalid field
            this.scrollToFirstInvalidField();
        }
        this.originalFormValues = this.claimForm.value;
    }

    updateCharacterCount(event: Event, fieldName: keyof typeof this.characterCounts): void {
        const input = event.target as HTMLTextAreaElement;
        this.characterCounts[fieldName] = input.value.length;
    }

    private initializeCharacterCounts(): void {
        // Initialize character counts based on existing form values
        for (const fieldName of Object.keys(this.characterCounts)) {
            const control = this.claimForm.get(fieldName);
            if (control?.value) {
                this.characterCounts[fieldName as keyof typeof this.characterCounts] = control.value.length;
            }
        }
    }

    fraudNotice() {
        this.modalService.open(FraudNoticesComponent, { scrollable: true, size: 'xl', centered: true, fullscreen: 'sm', windowClass: 'fraud-notices-modal' });
    }

    private buildClaimData(): ClaimPhysician {
        const claimPhysician = new ClaimPhysician();
        claimPhysician.id = this.claim.id;
        const physicianStudent = new Student();
        physicianStudent.firstName = this.claimForm.get('physicianStudent.firstName').value;
        physicianStudent.lastName = this.claimForm.get('physicianStudent.lastName').value;
        claimPhysician.physicianStudent = physicianStudent;

        claimPhysician.physicianSeparationReason = this.claimForm.get('physicianSeparationReason').value;
        claimPhysician.physicianICDDSM = this.claimForm.get('physicianICDDSM').value;
        claimPhysician.physicianClassAbsenceStartDate = this.dateParser.format(this.claimForm.get('physicianClassAbsenceStartDate').value);
        claimPhysician.physicianClassAbsenceEndDate = this.dateParser.format(this.claimForm.get('physicianClassAbsenceEndDate').value);
        claimPhysician.physicianFirstConsultDate = this.dateParser.format(this.claimForm.get('physicianFirstConsultDate').value);
        claimPhysician.physicianLastConsultDate = this.dateParser.format(this.claimForm.get('physicianLastConsultDate').value);
        claimPhysician.physicianHomeVisitCount = this.claimForm.get('physicianHomeVisitCount').value;
        claimPhysician.physicianOfficeVisitCount = this.claimForm.get('physicianOfficeVisitCount').value;
        claimPhysician.physicianHospitalVisitCount = this.claimForm.get('physicianHospitalVisitCount').value;
        claimPhysician.physicianStillUnderCare = this.claimForm.get('physicianStillUnderCare').value;
        claimPhysician.physicianReferredPhysicianInfo = this.claimForm.get('physicianReferredPhysicianInfo').value;
        claimPhysician.physicianReferringPhysicianInfo = this.claimForm.get('physicianReferringPhysicianInfo').value;
        claimPhysician.physicianWithdrawnOnRecommendation = this.claimForm.get('physicianWithdrawnOnRecommendation').value;
        claimPhysician.physicianWithdrawalRecommendationReason = this.claimForm.get('physicianWithdrawalRecommendationReason').value;
        claimPhysician.physicianAnticipatedReturnDescription = this.claimForm.get('physicianAnticipatedReturnDescription').value;
        claimPhysician.physicianAlternateSchoolOrEmploymentDuringLeave = this.claimForm.get('physicianAlternateSchoolOrEmploymentDuringLeave').value;
        claimPhysician.physicianWithPhysicianPermission = this.claimForm.get('physicianWithPhysicianPermission').value;
        claimPhysician.physicianAlternateActivityStartDate = this.dateParser.format(this.claimForm.get('physicianAlternateActivityStartDate').value);
        claimPhysician.physicianAlternateActivityExplanation = this.claimForm.get('physicianAlternateActivityExplanation').value;

        const physician = new Physician();
        physician.addresses = [];

        physician.firstName = this.claimForm.get('physician.firstName').value;
        physician.lastName = this.claimForm.get('physician.lastName').value;
        physician.phoneNumber = this.claimForm.get('physician.phoneNumber').value;
        physician.emailAddress = this.claimForm.get('physician.emailAddress').value;

        const address = new Address();
        address.address1 = this.claimForm.get('physician.address.address1').value;
        address.address2 = this.claimForm.get('physician.address.address2').value;
        address.city = this.claimForm.get('physician.address.city').value;
        address.stateAbbreviation = this.claimForm.get('physician.address.stateAbbreviation').value;
        address.countryISO3 = this.claimForm.get('physician.address.countryISO3').value;
        address.postalCode = this.claimForm.get('physician.address.postalCode').value;
        const addressTypeNew = new AddressType();
        addressTypeNew.id = GlobalConstants.ADDRESS_TYPES.MAILING_ADDRESS;
        addressTypeNew.description = '';
        address.addressType = addressTypeNew;
        physician.addresses.push(address);

        claimPhysician.physician = physician;

        claimPhysician.physicianLicenseNumber = this.claimForm.get('physicianLicenseNumber').value;

        claimPhysician.physicianSignature = this.claimForm.get('physicianSignature').value;

        LoggingService.log(claimPhysician);
        return claimPhysician;
    }

    private getMinimumSeparationDate(): NgbDateStruct {
        return { day: 1, month: 1, year: 2020 };
    }

    private getMaximumSeparationDate(): NgbDateStruct {
        return { day: 31, month: 12, year: 2050 };
    }

    private getMinimumConsultDate(): NgbDateStruct {
        return { day: 1, month: 1, year: 2000 };
    }

    private updateCountryCaptions(countryISO3: string) {
        if (countryISO3) {
            this.stateCaption = GlobalConstants.COUNTRY_METADATA[countryISO3].stateCaption;
            this.postalCodeCaption = GlobalConstants.COUNTRY_METADATA[countryISO3].postalCodeCaption;
            this.filteredStates = this.states.filter((state) => state.country.iso3 === countryISO3).sort((a, b) => a.stateAbbreviation.localeCompare(b.stateAbbreviation));
            this.changeDetector.detectChanges();
        }
    }

    private scrollToFirstInvalidField() {
        // Find the first invalid form control (input, select, textarea)
        const firstInvalidControl = document.querySelector('input.ng-invalid, textarea.ng-invalid, select.ng-invalid, app-signature-pad.ng-invalid');

        if (firstInvalidControl) {
            // Scroll to the control
            firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Set focus on the element
            if (firstInvalidControl instanceof HTMLElement) {
                setTimeout(() => {
                    // For standard form controls
                    if (firstInvalidControl instanceof HTMLInputElement || firstInvalidControl instanceof HTMLTextAreaElement || firstInvalidControl instanceof HTMLSelectElement) {
                        firstInvalidControl.focus();
                    }
                    // For custom components like signature-pad
                    else if (firstInvalidControl.tagName.toLowerCase() === 'app-signature-pad') {
                        // Find focusable element inside the custom component
                        const canvas = firstInvalidControl.querySelector('canvas');
                        if (canvas) {
                            canvas.focus();
                        }
                    }
                }, 500); // Small delay to ensure the scroll completes first
            }
        }
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        for (const key of Object.keys(formGroup.controls)) {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else if (control) {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        }
    }

    private applyZipCodeValidator(countryISO3: string) {
        const postalCodeControl = this.claimForm.get('physician.address.postalCode');
        if (postalCodeControl) {
            postalCodeControl.setValidators([Validators.required, ZipCodeValidator.validate(countryISO3)]);
            postalCodeControl.markAsTouched();
            postalCodeControl.updateValueAndValidity();
        }
    }

    ngOnDestroy(): void {
        if (this.claimSubscription) {
            this.claimSubscription.unsubscribe();
        }
    }
}
